import { useState, useRef } from 'react';
import supabase from '../../config/supabase';

/**
 * PhotoGalleryUpload
 * Image upload component with drag-and-drop, preview, reorder, and delete
 * 
 * Props:
 * - photos: string[] - Array of image URLs
 * - onPhotosChange: (photos: string[]) => void - Called when photos array changes
 * - maxPhotos?: number - Max number of photos (default 10)
 * - maxFileSize?: number - Max file size in MB (default 5)
 */
const PhotoGalleryUpload = ({
  photos = [],
  onPhotosChange,
  maxPhotos = 10,
  maxFileSize = 5,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  /**
   * Validate file before upload
   */
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxBytes = maxFileSize * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      return 'Only JPG, PNG, and WebP images are supported';
    }

    if (file.size > maxBytes) {
      return `File size must be less than ${maxFileSize}MB`;
    }

    return null;
  };

  /**
   * Upload file to Supabase storage
   */
  const uploadFileToSupabase = async (file) => {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const fileName = `${timestamp}-${randomString}-${file.name}`;
      const filePath = `business-photos/${fileName}`;

      // Upload file to Supabase storage
      const { data, error } = await supabase.storage
        .from('business-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw new Error(error.message || 'Upload failed');
      }

      // Get public URL
      const { data: publicData } = supabase.storage
        .from('business-photos')
        .getPublicUrl(filePath);

      if (!publicData?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      return publicData.publicUrl;
    } catch (error) {
      console.error('Supabase upload error:', error);
      throw error;
    }
  };

  /**
   * Process files
   */
  const processFiles = async (files) => {
    setError('');

    if (photos.length >= maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    const filesToProcess = Array.from(files).slice(0, maxPhotos - photos.length);
    const validFiles = [];

    // Validate all files first
    for (const file of filesToProcess) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      validFiles.push(file);
    }

    // Process valid files
    setUploading(true);
    try {
      const newUrls = [];

      // Upload files sequentially to avoid overwhelming the server
      for (const file of validFiles) {
        const url = await uploadFileToSupabase(file);
        newUrls.push(url);
      }

      onPhotosChange([...photos, ...newUrls]);
      setError('');
    } catch (err) {
      setError(
        err.message || 'Failed to upload images. Please check your connection and try again.'
      );
      console.error('File processing error:', err);
    } finally {
      setUploading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle drag over
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      dragCounterRef.current += 1;
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) {
        setDragActive(false);
      }
    }
  };

  /**
   * Handle drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFiles(e.dataTransfer.files);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFiles(e.target.files);
    }
  };

  /**
   * Delete photo
   */
  const deletePhoto = (index) => {
    const updated = photos.filter((_, i) => i !== index);
    onPhotosChange(updated);
  };

  /**
   * Move photo
   */
  const movePhoto = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= photos.length) return;

    const updated = [...photos];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    onPhotosChange(updated);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[12px] font-semibold text-slate-700 mb-2">
          Business Photos <span className="text-slate-400 font-normal">(up to {maxPhotos})</span>
        </label>

        {/* Upload Zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative px-4 py-6 border-2 border-dashed rounded-lg transition-all text-center cursor-pointer
            ${dragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-300 bg-slate-50 hover:border-slate-400'
            }
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileInputChange}
            disabled={uploading || photos.length >= maxPhotos}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          />

          <div className="space-y-1">
            <p className="text-[13px] font-medium text-slate-700">
              {uploading ? 'Uploading to cloud...' : 'Drag photos here or click to select'}
            </p>
            <p className="text-[11px] text-slate-500">
              JPG, PNG, WebP • Max {maxFileSize}MB each
            </p>
          </div>

          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
              <div className="flex flex-col items-center gap-2">
                <div className="h-5 w-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                <p className="text-[11px] text-slate-600">Processing...</p>
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <p className="text-[11px] text-red-600 mt-2 bg-red-50 border border-red-200 rounded px-2 py-1">
            {error}
          </p>
        )}

        {/* Progress indicator */}
        <div className="mt-2 flex items-center justify-between">
          <p className="text-[11px] text-slate-500">
            {photos.length} of {maxPhotos} photos added
          </p>
          {photos.length > 0 && (
            <div className="h-1.5 w-20 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${(photos.length / maxPhotos) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Gallery Preview */}
      {photos.length > 0 && (
        <div className="space-y-2">
          <p className="text-[12px] font-semibold text-slate-700">Preview</p>
          <div className="grid grid-cols-4 gap-2">
            {photos.map((photo, index) => (
              <div
                key={index}
                className="relative group bg-slate-100 rounded-lg overflow-hidden aspect-square border border-slate-200 hover:border-slate-300 transition-colors"
              >
                <img
                  src={photo}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />

                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  {/* Move up button */}
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => movePhoto(index, index - 1)}
                      title="Move up"
                      className="p-1.5 bg-white rounded hover:bg-slate-100 transition-colors"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    </button>
                  )}

                  {/* Move down button */}
                  {index < photos.length - 1 && (
                    <button
                      type="button"
                      onClick={() => movePhoto(index, index + 1)}
                      title="Move down"
                      className="p-1.5 bg-white rounded hover:bg-slate-100 transition-colors"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  )}

                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={() => deletePhoto(index)}
                    title="Delete"
                    className="p-1.5 bg-red-500 rounded hover:bg-red-600 transition-colors text-white"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </div>

                {/* Index badge */}
                <div className="absolute top-1 left-1 bg-slate-900/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Placeholder for empty state */}
      {photos.length === 0 && (
        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
          <p className="text-[11px] text-slate-500">No photos yet. Add images to showcase your business.</p>
        </div>
      )}
    </div>
  );
};

export default PhotoGalleryUpload;
